export type NavItem = {
  key: string;
  label: string;
  i18n?: Partial<Record<NavLocale, string>>;
  href?: string;
  action?: string;
  icon?: string;
};

export type NavSection = {
  key: string;
  title: string;
  titleI18n?: Partial<Record<NavLocale, string>>;
  sidebarTitle?: string;
  sidebarTitleI18n?: Partial<Record<NavLocale, string>>;
  icon?: string;
  items: NavItem[];
};

export const NAV_LOCALES = ["de-CH", "fr-CH", "it-CH", "en-CH"] as const;
export type NavLocale = (typeof NAV_LOCALES)[number];

export const NAV_SECTION_STORAGE_KEY = "nav_section_key";
export const NAV_SECTION_EVENT = "nav-section-change";

export const NAV_SECTIONS: NavSection[] = [
  {
    key: "buchen",
    title: "Buchen",
    titleI18n: { "fr-CH": "Compta", "it-CH": "Contabilita", "en-CH": "Posting" },
    icon: "🧾",
    items: [
      { key: "accounts", label: "Kontenplan", i18n: { "fr-CH": "Plan comptable", "it-CH": "Piano dei conti", "en-CH": "Chart of accounts" }, href: "/finance/accounts", icon: "🗂" },
      { key: "new-entry", label: "Neue Buchung", i18n: { "fr-CH": "Nouvelle ecriture", "it-CH": "Nuova registrazione", "en-CH": "New entry" }, href: "/finance/journal", icon: "🧾" },
      { key: "find-entry", label: "Buchungen suchen", i18n: { "fr-CH": "Rechercher", "it-CH": "Cerca registrazioni", "en-CH": "Search entries" }, href: "/finance/journal", icon: "🔎" },
      { key: "journal", label: "Journal", i18n: { "fr-CH": "Journal", "it-CH": "Giornale", "en-CH": "Journal" }, href: "/finance/journal", icon: "📒" },
      { key: "recurring", label: "Wiederkehrend", i18n: { "fr-CH": "Recurrent", "it-CH": "Ricorrente", "en-CH": "Recurring" }, action: "coming_soon_recurring", icon: "🔁" }
    ]
  },
  {
    key: "verkauf",
    title: "Debitoren",
    titleI18n: { "fr-CH": "Debiteurs", "it-CH": "Debitori", "en-CH": "Debtors" },
    sidebarTitle: "Debitoren / Kunden",
    sidebarTitleI18n: { "fr-CH": "Debiteurs / Clients", "it-CH": "Debitori / Clienti", "en-CH": "Debtors / Customers" },
    icon: "💼",
    items: [
      { key: "customers", label: "Kunden", i18n: { "fr-CH": "Clients", "it-CH": "Clienti", "en-CH": "Customers" }, href: "/finance/debtors", icon: "💼" },
      { key: "sales-invoices", label: "Rechnungen", i18n: { "fr-CH": "Factures", "it-CH": "Fatture", "en-CH": "Invoices" }, href: "/invoices/inbox?scope=debtor", icon: "🧾" },
      { key: "open-items-deb", label: "Offene Posten", i18n: { "fr-CH": "Postes ouverts", "it-CH": "Partite aperte", "en-CH": "Open items" }, href: "/finance/open-items?kind=DEBITOR", icon: "📌" }
    ]
  },
  {
    key: "einkauf",
    title: "Kreditoren",
    titleI18n: { "fr-CH": "Crediteurs", "it-CH": "Creditori", "en-CH": "Creditors" },
    sidebarTitle: "Kreditoren / Lieferanten",
    sidebarTitleI18n: { "fr-CH": "Crediteurs / Fournisseurs", "it-CH": "Creditori / Fornitori", "en-CH": "Creditors / Suppliers" },
    icon: "📥",
    items: [
      { key: "suppliers", label: "Lieferanten", i18n: { "fr-CH": "Fournisseurs", "it-CH": "Fornitori", "en-CH": "Suppliers" }, href: "/finance/creditors", icon: "📥" },
      { key: "purchase-invoices", label: "Eingangsrechnungen", i18n: { "fr-CH": "Factures d'achat", "it-CH": "Fatture acquisto", "en-CH": "Incoming invoices" }, href: "/invoices/inbox?scope=creditor", icon: "📄" },
      { key: "approvals", label: "Freigaben", i18n: { "fr-CH": "Validations", "it-CH": "Approvazioni", "en-CH": "Approvals" }, action: "coming_soon_approvals", icon: "✅" }
    ]
  },
  {
    key: "zahlungen",
    title: "Zahlungen",
    titleI18n: { "fr-CH": "Paiements", "it-CH": "Pagamenti", "en-CH": "Payments" },
    icon: "🏦",
    items: [
      { key: "bank", label: "Bank", i18n: { "fr-CH": "Banque", "it-CH": "Banca", "en-CH": "Bank" }, href: "/banking/reconciliation?view=bank", icon: "🏦" },
      { key: "cash", label: "Kasse", i18n: { "fr-CH": "Caisse", "it-CH": "Cassa", "en-CH": "Cash" }, action: "coming_soon_cash", icon: "💳" },
      { key: "reconciliation", label: "Abstimmung", i18n: { "fr-CH": "Rapprochement", "it-CH": "Riconciliazione", "en-CH": "Reconciliation" }, href: "/banking/reconciliation?view=reconciliation", icon: "🧩" }
    ]
  },
  {
    key: "berichte",
    title: "Berichte",
    titleI18n: { "fr-CH": "Rapports", "it-CH": "Report", "en-CH": "Reports" },
    icon: "📈",
    items: [
      { key: "balance-sheet", label: "Bilanz", i18n: { "fr-CH": "Bilan", "it-CH": "Stato patrimoniale", "en-CH": "Balance sheet" }, href: "/finance/reports/trial-balance", icon: "📈" },
      { key: "pnl", label: "Erfolgsrechnung", i18n: { "fr-CH": "Compte de resultat", "it-CH": "Conto economico", "en-CH": "Profit and loss" }, href: "/finance/reports/trial-balance", icon: "📉" },
      { key: "ledger", label: "Hauptbuch", i18n: { "fr-CH": "Grand livre", "it-CH": "Libro mastro", "en-CH": "General ledger" }, href: "/finance/journal", icon: "📚" }
    ]
  },
  {
    key: "stammdaten",
    title: "Mitarbeiter",
    titleI18n: { "fr-CH": "Employes", "it-CH": "Dipendenti", "en-CH": "Employees" },
    icon: "🗂",
    items: [
      { key: "employees", label: "Mitarbeiterstamm", i18n: { "fr-CH": "Base employes", "it-CH": "Anagrafica dipendenti", "en-CH": "Employee master" }, href: "/finance/payroll?view=employees", icon: "👤" },
      { key: "payroll", label: "Lohnabrechnungen", i18n: { "fr-CH": "Decomptes salariaux", "it-CH": "Conteggi salari", "en-CH": "Payroll slips" }, href: "/finance/payroll?view=sheets", icon: "👥" }
    ]
  },
  {
    key: "automation",
    title: "Automationen",
    titleI18n: { "fr-CH": "Automatisations", "it-CH": "Automazioni", "en-CH": "Automations" },
    icon: "⚙",
    items: [
      { key: "inbox-automation", label: "Inbox-Automation", i18n: { "fr-CH": "Automation inbox", "it-CH": "Automazione inbox", "en-CH": "Inbox automation" }, href: "/invoices/inbox?view=automation", icon: "📥" },
      { key: "daily-batch", label: "Tagesbatch", i18n: { "fr-CH": "Batch journalier", "it-CH": "Batch giornaliero", "en-CH": "Daily batch" }, href: "/finance/batch", icon: "📅" },
      { key: "vat-return", label: "MWST-Abrechnung", i18n: { "fr-CH": "Decompte TVA", "it-CH": "Conteggio IVA", "en-CH": "VAT return" }, href: "/finance/reports/vat", icon: "🧮" },
      { key: "rules-mappings", label: "Regeln & Mappings", i18n: { "fr-CH": "Regles et mappings", "it-CH": "Regole e mapping", "en-CH": "Rules & mappings" }, href: "/finance/vat", icon: "🧩" },
      { key: "approval-workflows", label: "Freigabe-Workflows", i18n: { "fr-CH": "Workflows de validation", "it-CH": "Workflow approvazione", "en-CH": "Approval workflows" }, action: "coming_soon_approval_workflows", icon: "✅" },
      { key: "ai-quality", label: "KI-Qualitaet", i18n: { "fr-CH": "Qualite IA", "it-CH": "Qualita IA", "en-CH": "AI quality" }, action: "coming_soon_ai_quality", icon: "🤖" },
      { key: "governance", label: "Governance / Audit", i18n: { "fr-CH": "Gouvernance / Audit", "it-CH": "Governance / Audit", "en-CH": "Governance / audit" }, href: "/finance/legal-monitor", icon: "🛡" }
    ]
  },
  {
    key: "admin",
    title: "Admin",
    titleI18n: { "fr-CH": "Admin", "it-CH": "Admin", "en-CH": "Admin" },
    icon: "🔐",
    items: [
      { key: "vat-codes", label: "MWST-Codes", i18n: { "fr-CH": "Codes TVA", "it-CH": "Codici IVA", "en-CH": "VAT codes" }, href: "/finance/vat", icon: "📊" },
      { key: "users", label: "Benutzer", i18n: { "fr-CH": "Utilisateurs", "it-CH": "Utenti", "en-CH": "Users" }, href: "/admin/users", icon: "🔐" },
      { key: "roles", label: "Rollen", i18n: { "fr-CH": "Roles", "it-CH": "Ruoli", "en-CH": "Roles" }, href: "/admin/roles", icon: "🛡" },
      { key: "company", label: "Firma", i18n: { "fr-CH": "Societe", "it-CH": "Azienda", "en-CH": "Company" }, href: "/admin/company", icon: "🏢" }
    ]
  }
];

export function pickLocaleText(locale: string, fallback: string, i18n?: Partial<Record<NavLocale, string>>): string {
  const code = NAV_LOCALES.includes(locale as NavLocale) ? (locale as NavLocale) : "de-CH";
  return i18n?.[code] || fallback;
}

export function resolveNavSectionKey(path: string): string {
  if (path === "/") return "buchen";
  if (path.startsWith("/finance/journal")) return "buchen";
  if (path.startsWith("/finance/debtors")) return "verkauf";
  if (path.startsWith("/finance/creditors")) return "einkauf";
  if (path.startsWith("/banking")) return "zahlungen";
  if (path.startsWith("/finance/vat")) return "admin";
  if (path.startsWith("/finance/reports/vat")) return "automation";
  if (path.startsWith("/finance/reports")) return "berichte";
  if (path.startsWith("/finance/accounts")) return "buchen";
  if (path.startsWith("/finance/payroll")) return "stammdaten";
  if (path.startsWith("/finance/batch")) return "automation";
  if (path.startsWith("/finance/legal-monitor")) return "automation";
  if (path.startsWith("/admin")) return "admin";
  return "buchen";
}

